import React, { useRef } from "react"
import { useIntersectionObserver } from 'react-intersection-observer-hook';
import { Flex, Box, Image } from "rebass"
import styled from "@emotion/styled"

import Reveal from "../reveals"

const StandardMediaGrid = ({ gap, columns, children }) => {
  return (
    <Box
      sx={{
        display: "grid",
        gridGap: gap,
        gridTemplateColumns: ['1fr', null, `repeat(${columns}, 1fr)`],
      }}
    >
      {children}
    </Box>
  )
}

// 6x2 custom grid
const CustomEsmaMediaGrid = ({ children }) => {
  return (
    <Box
      as={EsmaMediaGrid}
      sx={{
        display: "grid",
        gridGap: 0,
        gridTemplateColumns: "repeat(6, 1fr) 1.5fr",
        gridAutoRows: "1fr",
        paddingRight: [0, null, null, 5],
      }}
    >
      {children}
    </Box>
  )
}

// 2x2 custom grid
const CustomEsmaMediaGridSquare = ({ children }) => {
  return (
    <Box
      as={EsmaMediaGridSquare}
      sx={{
        display: "grid",
        gridGap: 0,
        gridTemplateColumns: "repeat(6, 1fr)",
        gridAutoRows: "1fr",
        paddingLeft: [0, '4vw'],
        paddingRight: [0, '4vw'],
      }}
    >
      {children}
    </Box>
  )
}

const MediaItemVideo = ({ media, index, revealAnimation }) => {
  const { image, video, videoMobile } = media
  const videoRef = useRef()
  const [ref, { entry }] = useIntersectionObserver();
  const isVisible = entry && entry.isIntersecting;

  const onMouseEnter = () => {
    videoRef.current.play();
  }
  const onMouseLeave = () => {
    videoRef.current.pause();
  }

  return (
    <GridCell ref={ref}>
      <Reveal delay={0.15 * index} animation={revealAnimation}>
        <Box
          as={Video}
          ref={videoRef}
          width={"100%"}
          muted
          preload="metadata"
          loop
          playsInline
          disableremoteplayback="true"
          onMouseEnter={image ? onMouseEnter : null}
          onMouseLeave={image ? onMouseLeave : null}
          poster={image ? image.sourceUrl : null}
          autoPlay={image ? false : true}
        >
          { videoMobile && isVisible && <source src={video.mediaItemUrl} media="(min-width: 992px)" /> }
          { videoMobile && isVisible && <source src={videoMobile.mediaItemUrl} /> }
          { !videoMobile && isVisible && <source src={video.mediaItemUrl} />}
        </Box>
      </Reveal>
    </GridCell>
  )
}

const MediaItemImage = ({ media, index, revealAnimation }) => {
  const { image } = media
  return (
    <GridCell>
      <Reveal delay={0.15 * index} animation={revealAnimation}>
        <Image alt={image.title} srcSet={image.srcSet} />
      </Reveal>
    </GridCell>
  )
}

const MediaItemShape = ({ media, index }) => {
  const { shape, color } = media

  // pick container style
  const style = shape === `circle` ? ShapeCircle : ShapeSquare
  const align = shape === `circle` ? "flex-end" : "flex-start"
  return (
    <GridCell>
      <Reveal delay={0.15 * index} animation={"scale-in"}>
        <Flex height="100%" alignItems={align}>
          <Box as={style} backgroundColor={color} />
        </Flex>
      </Reveal>
    </GridCell>
  )
}

export const MEDIA_TYPES = {
  video: MediaItemVideo,
  image: MediaItemImage,
  shape: MediaItemShape,
}

export const GRID_TYPES = {
  standard: StandardMediaGrid,
  "custom-esma": CustomEsmaMediaGrid,
  "custom-esma-square": CustomEsmaMediaGridSquare,
}

const MediasGallery = props => {
  const { grid, medias, revealAnimation } = props

  let GridType = GRID_TYPES[grid]
  if (!GridType) GridType = StandardMediaGrid

  return (
    <GridType {...props}>
      {medias.map((media, index) => {
        const ComponentType = MEDIA_TYPES[media.type]
        return ComponentType ? (
          <ComponentType key={index} index={index} revealAnimation={revealAnimation} media={media} />
        ) : (
          <>not implemented</>
        )
      })}
    </GridType>
  )
}

export default MediasGallery

// component styles

const GridCell = styled.div`
  img,
  video {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const ShapeSquare = styled.span`
  display: block;
  width: 100%;
  padding-bottom: 100%;
`

const ShapeCircle = styled.span`
  display: block;
  width: 100%;
  padding-bottom: 100%;
  border-radius: ${props => props.theme.radii.circle};
`

const Video = styled.video``

const EsmaMediaGrid = styled.div`
  ${GridCell}:nth-child(6n + 1) {
    grid-column: 1 / span 2;
  }
  ${GridCell}:nth-child(6n + 2) {
    grid-column: 4 / span 2;
  }
  ${GridCell}:nth-child(6n + 3) {
    grid-column: 6 / span 1;
  }
  ${GridCell}:nth-child(6n + 4) {
    grid-column: 2 / span 2;
  }
  ${GridCell}:nth-child(6n + 5) {
    grid-column: 5 / span 3;
    grid-row-end: span 2;
  }
`

const EsmaMediaGridSquare = styled.div`
  ${GridCell}:nth-child(4n + 1) {
    grid-column: 1 / span 2;
  }
  ${GridCell}:nth-child(4n + 2) {
    grid-column: 3 / span 4;
  }
  ${GridCell}:nth-child(4n + 3) {
    grid-column: 1 / span 4;
  }
  ${GridCell}:nth-child(4n + 4) {
    grid-column: 5 / span 4;
  }
`
